import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, API, getActiveAccountHeader } from '../App';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, Activity, Calendar, DollarSign, Clock, 
  Syringe, Scissors, Stethoscope, ChevronRight, Trophy, Trash2, X, Send, Target, User, Lock,
  TrendingUp, AlertCircle, CheckCircle2, Camera, Loader2
} from 'lucide-react';
import { format, parseISO, isBefore, addDays } from 'date-fns';

// Helper to format record type for display
const formatRecordType = (recordType) => {
  const typeMap = {
    'appointment': 'Appointment',
    'vaccination': 'Vaccination',
    'worming': 'Worming',
    'farrier': 'Farrier',
    'dentist': 'Dentist',
    'veterinary': 'Veterinary',
    'medication': 'Medication',
    'insemination': 'Insemination',
    'pregnancy_scan': 'Breeding Scan',
    'foaling': 'Foaling',
    'other': 'Other'
  };
  return typeMap[recordType] || recordType?.replace(/_/g, ' ') || recordType;
};

// Form components defined OUTSIDE to prevent re-render issues
const DASHBOARD_RIDE_TYPES = [
  { value: 'flatwork', label: 'Flatwork' },
  { value: 'jumping', label: 'Jumping' },
  { value: 'trail', label: 'Trail' },
  { value: 'lesson', label: 'Lesson' },
  { value: 'hack', label: 'Hack' },
  { value: 'competition', label: 'Competition' },
  { value: 'dressage', label: 'Dressage' },
  { value: 'lunging', label: 'Lunging' },
  { value: 'trackwork', label: 'Trackwork' }
];

const RideForm = ({ data, onChange, onSubmit, horses, riders }) => {
  const [showCustomType, setShowCustomType] = useState(false);
  const [customTypeInput, setCustomTypeInput] = useState('');
  const [showOtherRider, setShowOtherRider] = useState(false);
  
  const selectedTypes = data.ride_types || (data.ride_type ? [data.ride_type] : []);
  
  const handleTypeToggle = (typeValue) => {
    const newTypes = selectedTypes.includes(typeValue)
      ? selectedTypes.filter(t => t !== typeValue)
      : [...selectedTypes, typeValue];
    onChange({ ...data, ride_types: newTypes });
  };
  
  const handleAddCustomType = () => {
    if (customTypeInput.trim()) {
      const newTypes = [...selectedTypes, customTypeInput.trim()];
      onChange({ ...data, ride_types: newTypes, custom_ride_type: customTypeInput.trim() });
      setCustomTypeInput('');
      setShowCustomType(false);
    }
  };

  const handleRiderChange = (value) => {
    if (value === 'other') {
      setShowOtherRider(true);
      onChange({ ...data, rider: '' });
    } else {
      setShowOtherRider(false);
      onChange({ ...data, rider: value });
    }
  };
  
  return (
    <form onSubmit={onSubmit} className="space-y-4 mt-4">
      <div className="space-y-2">
        <Label>Horse *</Label>
        <Select value={data.horse_id || ''} onValueChange={(v) => onChange({ ...data, horse_id: v })}>
          <SelectTrigger data-testid="quick-ride-horse-select">
            <SelectValue placeholder="Select horse" />
          </SelectTrigger>
          <SelectContent>
            {horses.map(horse => (
              <SelectItem key={horse.horse_id} value={horse.horse_id}>
                {horse.stable_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Rider *</Label>
        {!showOtherRider ? (
          <Select value={data.rider || ''} onValueChange={handleRiderChange}>
            <SelectTrigger data-testid="quick-ride-rider-select">
              <SelectValue placeholder="Select rider" />
            </SelectTrigger>
            <SelectContent>
              {riders.map(rider => (
                <SelectItem key={rider.rider_id} value={rider.name}>
                  {rider.name}
                </SelectItem>
              ))}
              <SelectItem value="other">Other (type name)</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <div className="flex gap-2">
            <Input 
              value={data.rider || ''} 
              onChange={(e) => onChange({ ...data, rider: e.target.value })} 
              placeholder="Enter rider name"
              required 
              data-testid="quick-ride-rider-input" 
            />
            <Button type="button" variant="outline" size="icon" onClick={() => setShowOtherRider(false)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Date *</Label>
          <Input type="date" value={data.date || ''} onChange={(e) => onChange({ ...data, date: e.target.value })} required data-testid="quick-ride-date-input" />
        </div>
        <div className="space-y-2">
          <Label>Duration (min)</Label>
          <Input type="number" value={data.duration || ''} onChange={(e) => onChange({ ...data, duration: e.target.value })} data-testid="quick-ride-duration-input" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Ride Type(s)</Label>
        <div className="flex flex-wrap gap-2">
          {DASHBOARD_RIDE_TYPES.map(opt => (
            <Button
              key={opt.value}
              type="button"
              variant={selectedTypes.includes(opt.value) ? "default" : "outline"}
              size="sm"
              onClick={() => handleTypeToggle(opt.value)}
              className={selectedTypes.includes(opt.value) ? 'bg-forest hover:bg-forest-dark' : ''}
              data-testid={`quick-ride-type-${opt.value}`}
            >
              {opt.label}
            </Button>
          ))}
          {selectedTypes.filter(t => !DASHBOARD_RIDE_TYPES.find(o => o.value === t)).map(customType => (
            <Button
              key={customType}
              type="button"
              variant="default"
              size="sm"
              onClick={() => handleTypeToggle(customType)}
              className="bg-saddle hover:bg-saddle/80"
            >
              {customType} ✕
            </Button>
          ))}
        </div>
        {!showCustomType ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowCustomType(true)} className="text-forest" data-testid="quick-ride-add-custom-type">
            + Add Custom Type
          </Button>
        ) : (
          <div className="flex gap-2 mt-2">
            <Input 
              value={customTypeInput} 
              onChange={(e) => setCustomTypeInput(e.target.value)} 
              placeholder="Custom type name"
              className="flex-1"
              data-testid="quick-ride-custom-type-input"
            />
            <Button type="button" onClick={handleAddCustomType} size="sm" className="bg-forest hover:bg-forest-dark">Add</Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => { setShowCustomType(false); setCustomTypeInput(''); }}>Cancel</Button>
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={data.notes || ''} onChange={(e) => onChange({ ...data, notes: e.target.value })} data-testid="quick-ride-notes-input" />
      </div>
      <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="quick-ride-submit-btn">Log Ride</Button>
    </form>
  );
};

const HealthForm = ({ data, onChange, onSubmit, horses, selectedHorses, setSelectedHorses }) => {
  const multiSelectTypes = ['farrier', 'vaccination', 'worming', 'dentist'];
  const isMultiSelect = multiSelectTypes.includes(data.record_type);

  const handleHorseToggle = (horseId) => {
    if (selectedHorses.includes(horseId)) {
      setSelectedHorses(selectedHorses.filter(id => id !== horseId));
    } else {
      setSelectedHorses([...selectedHorses, horseId]);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4 mt-4">
      <div className="space-y-2">
        <Label>Record Type</Label>
        <Select value={data.record_type} onValueChange={(v) => {
          onChange({ ...data, record_type: v });
          if (!multiSelectTypes.includes(v)) {
            setSelectedHorses([]);
          }
          // Reset sub-types when changing record type
          if (v === 'veterinary') {
            onChange({ ...data, record_type: v, vet_visit_type: 'checkup', farrier_type: null, sire: null, service_type: null, scan_result: null });
          } else if (v === 'farrier') {
            onChange({ ...data, record_type: v, vet_visit_type: null, farrier_type: 'shoeing', sire: null, service_type: null, scan_result: null });
          } else {
            onChange({ ...data, record_type: v, vet_visit_type: null, farrier_type: null, sire: null, service_type: null, scan_result: null });
          }
        }}>
          <SelectTrigger data-testid="quick-health-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="vaccination">Vaccination</SelectItem>
            <SelectItem value="worming">Worming</SelectItem>
            <SelectItem value="farrier">Farrier</SelectItem>
            <SelectItem value="dentist">Dentist</SelectItem>
            <SelectItem value="veterinary">Veterinary</SelectItem>
            <SelectItem value="gelding">Gelding</SelectItem>
            <SelectItem value="medication">Medication</SelectItem>
            <SelectItem value="insemination">Insemination</SelectItem>
            <SelectItem value="pregnancy_scan">Breeding Scan</SelectItem>
            <SelectItem value="foaling">Foaling</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {data.record_type === 'veterinary' && (
        <div className="space-y-2">
          <Label>Visit Type</Label>
          <Select value={data.vet_visit_type || 'checkup'} onValueChange={(v) => onChange({ ...data, vet_visit_type: v })}>
            <SelectTrigger data-testid="quick-health-vet-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="illness">Illness</SelectItem>
              <SelectItem value="injury">Injury</SelectItem>
              <SelectItem value="pre_purchase">Pre-Purchase Examination</SelectItem>
              <SelectItem value="checkup">Check Up</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {data.record_type === 'farrier' && (
        <div className="space-y-2">
          <Label>Service Type</Label>
          <Select value={data.farrier_type || 'shoeing'} onValueChange={(v) => onChange({ ...data, farrier_type: v })}>
            <SelectTrigger data-testid="quick-health-farrier-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="shoeing">Shoeing</SelectItem>
              <SelectItem value="trim">Trim</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {data.record_type === 'insemination' && (
        <>
          <div className="space-y-2">
            <Label>Sire</Label>
            <Input value={data.sire || ''} onChange={(e) => onChange({ ...data, sire: e.target.value })} placeholder="Stallion name" data-testid="quick-health-sire-input" />
          </div>
          <div className="space-y-2">
            <Label>Service Type</Label>
            <Select value={data.service_type || ''} onValueChange={(v) => onChange({ ...data, service_type: v })}>
              <SelectTrigger data-testid="quick-health-service-type-select"><SelectValue placeholder="Select service type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="natural">Natural</SelectItem>
                <SelectItem value="ai_fresh">AI - Fresh</SelectItem>
                <SelectItem value="ai_frozen">AI - Frozen</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {data.record_type === 'pregnancy_scan' && (
        <div className="space-y-2">
          <Label>Scan Result</Label>
          <Select value={data.scan_result || ''} onValueChange={(v) => onChange({ ...data, scan_result: v })}>
            <SelectTrigger data-testid="quick-health-scan-result-select"><SelectValue placeholder="Select result" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="positive">Positive Pregnancy</SelectItem>
              <SelectItem value="negative">Negative Pregnancy</SelectItem>
              <SelectItem value="routine">Routine Scan</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Gelding - info note about auto-history */}
      {data.record_type === 'gelding' && (
        <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-sm text-purple-700">
          This will also be added to the horse's History timeline.
        </div>
      )}

      {/* Foaling fields */}
      {data.record_type === 'foaling' && (
        <>
          <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 text-sm text-orange-700">
            This will also be added to the horse's History timeline.
          </div>
          <div className="space-y-2">
            <Label>Sire (Stallion)</Label>
            <Input value={data.sire || ''} onChange={(e) => onChange({ ...data, sire: e.target.value })} placeholder="Stallion name" data-testid="quick-foaling-sire-input" />
          </div>
          <div className="space-y-2">
            <Label>Foal's Name</Label>
            <Input value={data.foal_name || ''} onChange={(e) => onChange({ ...data, foal_name: e.target.value })} placeholder="Foal name (if known)" data-testid="quick-foaling-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Outcome</Label>
              <Select value={data.outcome || ''} onValueChange={(v) => onChange({ ...data, outcome: v })}>
                <SelectTrigger data-testid="quick-foaling-outcome-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="live_foal">Live Foal</SelectItem>
                  <SelectItem value="stillbirth">Stillbirth</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Foal Sex</Label>
              <Select value={data.foal_sex || ''} onValueChange={(v) => onChange({ ...data, foal_sex: v })}>
                <SelectTrigger data-testid="quick-foaling-sex-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="colt">Colt</SelectItem>
                  <SelectItem value="filly">Filly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Number of Foals</Label>
              <Select value={data.number_of_foals || ''} onValueChange={(v) => onChange({ ...data, number_of_foals: v })}>
                <SelectTrigger data-testid="quick-foaling-count-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single</SelectItem>
                  <SelectItem value="twin">Twin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Delivery Type</Label>
              <Select value={data.delivery_type || ''} onValueChange={(v) => onChange({ ...data, delivery_type: v })}>
                <SelectTrigger data-testid="quick-foaling-delivery-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal Foaling</SelectItem>
                  <SelectItem value="assisted">Assisted Delivery</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </>
      )}

      {isMultiSelect ? (
        <div className="space-y-2">
          <Label>Select Horses (multiple allowed)</Label>
          <div className="border rounded-md p-3 space-y-2 max-h-48 overflow-y-auto">
            {horses.map(horse => (
              <div key={horse.horse_id} className="flex items-center space-x-2">
                <Checkbox
                  id={`horse-${horse.horse_id}`}
                  checked={selectedHorses.includes(horse.horse_id)}
                  onCheckedChange={() => handleHorseToggle(horse.horse_id)}
                  data-testid={`quick-health-horse-${horse.horse_id}`}
                />
                <label htmlFor={`horse-${horse.horse_id}`} className="text-sm cursor-pointer">
                  {horse.stable_name}
                </label>
              </div>
            ))}
          </div>
          {selectedHorses.length > 0 && (
            <p className="text-sm text-muted-foreground">{selectedHorses.length} horse(s) selected</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <Label>Horse *</Label>
          <Select value={data.horse_id || ''} onValueChange={(v) => onChange({ ...data, horse_id: v })}>
            <SelectTrigger data-testid="quick-health-horse-select">
              <SelectValue placeholder="Select horse" />
            </SelectTrigger>
            <SelectContent>
              {horses.map(horse => (
                <SelectItem key={horse.horse_id} value={horse.horse_id}>
                  {horse.stable_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <Label>Provider</Label>
        <Input value={data.provider || ''} onChange={(e) => onChange({ ...data, provider: e.target.value })} placeholder="Vet/Farrier name" data-testid="quick-health-provider-input" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Date *</Label>
          <Input type="date" value={data.date || ''} onChange={(e) => onChange({ ...data, date: e.target.value })} required data-testid="quick-health-date-input" />
        </div>
        <div className="space-y-2">
          <Label>Next Due</Label>
          <Input type="date" value={data.next_due_date || ''} onChange={(e) => onChange({ ...data, next_due_date: e.target.value })} data-testid="quick-health-nextdue-input" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={data.notes || ''} onChange={(e) => onChange({ ...data, notes: e.target.value })} data-testid="quick-health-notes-input" />
      </div>
      <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="quick-health-submit-btn">
        Add Record{isMultiSelect && selectedHorses.length > 1 ? 's' : ''}
      </Button>
    </form>
  );
};

const ExpenseForm = ({ data, onChange, onSubmit, horses }) => (
  <form onSubmit={onSubmit} className="space-y-4 mt-4">
    <div className="space-y-2">
      <Label>Horse</Label>
      <Select value={data.horse_id || 'general'} onValueChange={(v) => onChange({ ...data, horse_id: v === 'general' ? null : v })}>
        <SelectTrigger data-testid="quick-expense-horse-select">
          <SelectValue placeholder="Select horse" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="general">General (Not tied to specific horse)</SelectItem>
          {horses.map(horse => (
            <SelectItem key={horse.horse_id} value={horse.horse_id}>
              {horse.stable_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">Use "General" for bulk feed orders, facility costs, etc.</p>
    </div>
    <div className="space-y-2">
      <Label>Category *</Label>
      <Select value={data.category || ''} onValueChange={(v) => onChange({ ...data, category: v })}>
        <SelectTrigger data-testid="quick-expense-category-select">
          <SelectValue placeholder="Select category" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="vet">Vet</SelectItem>
          <SelectItem value="farrier">Farrier</SelectItem>
          <SelectItem value="feed">Feed</SelectItem>
          <SelectItem value="gear">Tack</SelectItem>
          <SelectItem value="entry_fee">Entry Fee</SelectItem>
          <SelectItem value="boarding">Boarding</SelectItem>
          <SelectItem value="training">Training</SelectItem>
          <SelectItem value="lesson">Lesson</SelectItem>
          <SelectItem value="other">Other</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Amount *</Label>
        <Input type="number" step="0.01" value={data.amount || ''} onChange={(e) => onChange({ ...data, amount: e.target.value })} required data-testid="quick-expense-amount-input" />
      </div>
      <div className="space-y-2">
        <Label>Date *</Label>
        <Input type="date" value={data.date || ''} onChange={(e) => onChange({ ...data, date: e.target.value })} required data-testid="quick-expense-date-input" />
      </div>
    </div>
    <div className="space-y-2">
      <Label>Vendor</Label>
      <Input value={data.vendor || ''} onChange={(e) => onChange({ ...data, vendor: e.target.value })} data-testid="quick-expense-vendor-input" />
    </div>
    <div className="space-y-2">
      <Label>Notes</Label>
      <Textarea value={data.notes || ''} onChange={(e) => onChange({ ...data, notes: e.target.value })} data-testid="quick-expense-notes-input" />
    </div>
    <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="quick-expense-submit-btn">Add Expense</Button>
  </form>
);

const EventForm = ({ data, onChange, onSubmit, horses, selectedHorses, setSelectedHorses }) => {
  const handleHorseToggle = (horseId) => {
    if (selectedHorses.includes(horseId)) {
      setSelectedHorses(selectedHorses.filter(id => id !== horseId));
    } else {
      setSelectedHorses([...selectedHorses, horseId]);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4 mt-4">
      <div className="space-y-2">
        <Label>Select Horses Attending (multiple allowed)</Label>
        <div className="border rounded-md p-3 space-y-2 max-h-48 overflow-y-auto">
          {horses.map(horse => (
            <div key={horse.horse_id} className="flex items-center space-x-2">
              <Checkbox
                id={`event-horse-${horse.horse_id}`}
                checked={selectedHorses.includes(horse.horse_id)}
                onCheckedChange={() => handleHorseToggle(horse.horse_id)}
                data-testid={`quick-event-horse-${horse.horse_id}`}
              />
              <label htmlFor={`event-horse-${horse.horse_id}`} className="text-sm cursor-pointer">
                {horse.stable_name}
              </label>
            </div>
          ))}
        </div>
        {selectedHorses.length > 0 && (
          <p className="text-sm text-muted-foreground">{selectedHorses.length} horse(s) selected</p>
        )}
      </div>
      <div className="space-y-2">
        <Label>Event Name *</Label>
        <Input value={data.event_name || ''} onChange={(e) => onChange({ ...data, event_name: e.target.value })} required data-testid="quick-event-name-input" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Date *</Label>
          <Input type="date" value={data.date || ''} onChange={(e) => onChange({ ...data, date: e.target.value })} required data-testid="quick-event-date-input" />
        </div>
        <div className="space-y-2">
          <Label>Location</Label>
          <Input value={data.location || ''} onChange={(e) => onChange({ ...data, location: e.target.value })} data-testid="quick-event-location-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Discipline</Label>
          <Input value={data.discipline || ''} onChange={(e) => onChange({ ...data, discipline: e.target.value })} placeholder="e.g., Dressage" data-testid="quick-event-discipline-input" />
        </div>
        <div className="space-y-2">
          <Label>Class</Label>
          <Input value={data.event_class || ''} onChange={(e) => onChange({ ...data, event_class: e.target.value })} data-testid="quick-event-class-input" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={data.notes || ''} onChange={(e) => onChange({ ...data, notes: e.target.value })} data-testid="quick-event-notes-input" />
      </div>
      <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="quick-event-submit-btn">
        Add Event{selectedHorses.length > 1 ? 's' : ''}
      </Button>
    </form>
  );
};

export const Dashboard = () => {
  const { user, token } = useAuth();
  const { canAddEdit, requireSubscription } = useSubscription();
  const location = useLocation();
  const [reminders, setReminders] = useState([]);
  const [recentActivity, setRecentActivity] = useState({ rides: [], expenses: [], events: [] });
  const [horses, setHorses] = useState([]);
  const [riders, setRiders] = useState([]);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Handle account switch from URL parameter (iOS Safari compatibility)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const switchedAccount = params.get('switched');
    if (switchedAccount) {
      // Ensure storage is set correctly
      localStorage.setItem('activeAccountId', switchedAccount);
      sessionStorage.setItem('activeAccountId', switchedAccount);
      // Clean URL without reload
      window.history.replaceState({}, '', '/dashboard');
    }
  }, [location.search]);

  // Compute default owner name from user
  const defaultOwnerName = user?.use_business_as_owner && user?.business_name 
    ? user.business_name 
    : user?.name || '';

  // Dialog states
  const [rideDialogOpen, setRideDialogOpen] = useState(false);
  const [healthDialogOpen, setHealthDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [addHorseDialogOpen, setAddHorseDialogOpen] = useState(false);
  const [isAddingHorse, setIsAddingHorse] = useState(false);
  
  // Profile photo state for new horse
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const profilePhotoInputRef = useRef(null);
  
  // View/Edit dialog states
  const [viewRideDialogOpen, setViewRideDialogOpen] = useState(false);
  const [viewHealthDialogOpen, setViewHealthDialogOpen] = useState(false);
  const [viewExpenseDialogOpen, setViewExpenseDialogOpen] = useState(false);
  const [viewEventDialogOpen, setViewEventDialogOpen] = useState(false);
  
  // Selected items for viewing/editing
  const [selectedRide, setSelectedRide] = useState(null);
  const [selectedHealth, setSelectedHealth] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Form states
  const [newRide, setNewRide] = useState({ horse_id: '', rider: '', date: '', ride_types: [], duration: '', notes: '' });
  const [newHealth, setNewHealth] = useState({ horse_id: '', record_type: 'vaccination', vet_visit_type: null, farrier_type: null, provider: '', date: '', next_due_date: '', notes: '' });
  const [newExpense, setNewExpense] = useState({ horse_id: null, category: '', amount: '', vendor: '', date: '', notes: '' });
  const [newEvent, setNewEvent] = useState({ event_name: '', location: '', discipline: '', event_class: '', date: '', notes: '' });
  const [newHorse, setNewHorse] = useState({ 
    registered_name: '', 
    stable_name: '', 
    breed: '', 
    colour: '', 
    sex: '', 
    dob: '',
    height: '',
    microchip: '',
    registration_number: '',
    registration_society: '',
    sire: '',
    dam: '',
    breeder: '',
    owner: ''
  });
  
  // Multi-select horse states
  const [selectedHealthHorses, setSelectedHealthHorses] = useState([]);
  const [selectedEventHorses, setSelectedEventHorses] = useState([]);

  // Get headers dynamically each time to ensure fresh activeAccountId
  const getHeaders = () => {
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const h = { 'Content-Type': 'application/json' };
    if (activeAccountId) h['X-Active-Account'] = activeAccountId;
    if (token) h['Authorization'] = `Bearer ${token}`;
    console.log('Dashboard API call - activeAccountId:', activeAccountId, 'user:', user?.email, 'user_id:', user?.user_id);
    return h;
  };

  const fetchData = async () => {
    const headers = getHeaders();
    // Add cache-busting parameter to force fresh data
    const cacheBuster = `?_t=${Date.now()}`;
    try {
      const [remindersRes, activityRes, horsesRes, ridersRes] = await Promise.all([
        fetch(`${API}/dashboard/reminders${cacheBuster}`, { headers, cache: 'no-store' }),
        fetch(`${API}/dashboard/recent${cacheBuster}`, { headers, cache: 'no-store' }),
        fetch(`${API}/horses${cacheBuster}`, { headers, cache: 'no-store' }),
        fetch(`${API}/rider-profiles${cacheBuster}`, { headers, cache: 'no-store' })
      ]);

      if (remindersRes.ok) {
        const data = await remindersRes.json();
        setReminders(data.upcoming_health || []);
      }

      if (activityRes.ok) {
        const data = await activityRes.json();
        console.log('Dashboard recent_rides received:', data.recent_rides?.length, data.recent_rides);
        console.log('Dashboard recent_activity received:', data.recent_activity?.length, data.recent_activity);
        setRecentActivity({
          rides: data.recent_rides || [],
          expenses: data.recent_expenses || [],
          events: data.upcoming_events || [],
          recent_activity: data.recent_activity || [],
          recent_expenses: data.recent_expenses || []
        });
      } else {
        console.log('Activity response NOT OK:', activityRes.status);
      }

      if (horsesRes.ok) {
        const data = await horsesRes.json();
        setHorses(data.filter(h => h.status === 'current' || h.horse_tab !== 'former'));
      }

      if (ridersRes.ok) {
        setRiders(await ridersRes.json());
      }

      // Fetch pending transfers
      const transfersRes = await fetch(`${API}/transfers`, {  headers });
      if (transfersRes.ok) {
        const transfers = await transfersRes.json();
        const pending = transfers.filter(t => t.status === 'pending' && t.to_user_email === user?.email);
        setPendingTransfers(pending);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  // Set default owner when dialog opens or user changes
  useEffect(() => {
    if (addHorseDialogOpen && defaultOwnerName) {
      setNewHorse(prev => ({ ...prev, owner: prev.owner || defaultOwnerName }));
    }
  }, [addHorseDialogOpen, defaultOwnerName]);

  // Add Horse Handler
  const handleAddHorse = async (e) => {
    e.preventDefault();
    if (!newHorse.registered_name || !newHorse.stable_name) {
      toast.error('Please fill in required fields');
      return;
    }
    setIsAddingHorse(true);
    try {
      const headers = getHeaders();
      const response = await fetch(`${API}/horses`, {
        method: 'POST',
        headers,
        body: JSON.stringify(newHorse)
      });
      if (response.ok) {
        const horse = await response.json();
        
        // Upload profile photo if one was selected
        if (profilePhotoFile) {
          try {
            const formData = new FormData();
            formData.append('file', profilePhotoFile);
            
            const uploadHeaders = {};
            const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
            if (activeAccountId) uploadHeaders['X-Active-Account'] = activeAccountId;
            if (token) uploadHeaders['Authorization'] = `Bearer ${token}`;
            
            await fetch(`${API}/upload/profile/${horse.horse_id}`, {
              method: 'POST',
              headers: uploadHeaders,
              body: formData
            });
          } catch (uploadError) {
            console.error('Failed to upload profile photo:', uploadError);
            toast.info('Horse created, but photo upload failed. You can add a photo from the horse profile.');
          }
        }
        
        toast.success('Horse added!');
        setAddHorseDialogOpen(false);
        setNewHorse({ 
          registered_name: '', stable_name: '', breed: '', colour: '', sex: '', dob: '',
          height: '', microchip: '', registration_number: '', registration_society: '',
          sire: '', dam: '', breeder: '', owner: defaultOwnerName
        });
        setProfilePhotoFile(null);
        setProfilePhotoPreview(null);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to add horse');
    } finally {
      setIsAddingHorse(false);
    }
  };
  
  // Handle profile photo selection for Add Horse
  const handleProfilePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image must be less than 10MB');
        return;
      }
      setProfilePhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Clear profile photo
  const clearProfilePhoto = () => {
    setProfilePhotoFile(null);
    setProfilePhotoPreview(null);
    if (profilePhotoInputRef.current) {
      profilePhotoInputRef.current.value = '';
    }
  };

  // Quick Add Handlers
  const handleAddRide = async (e) => {
    e.preventDefault();
    if (!newRide.horse_id) {
      toast.error('Please select a horse');
      return;
    }
    try {
      const headers = getHeaders();
      const response = await fetch(`${API}/ride-logs`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...newRide, duration: newRide.duration ? parseInt(newRide.duration) : null })
      });
      if (response.ok) {
        toast.success('Ride logged!');
        setRideDialogOpen(false);
        setNewRide({ horse_id: '', rider: '', date: '', ride_types: [], duration: '', notes: '' });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to log ride');
    }
  };

  const handleAddHealth = async (e) => {
    e.preventDefault();
    const multiSelectTypes = ['farrier', 'vaccination', 'worming', 'dentist'];
    const isMultiSelect = multiSelectTypes.includes(newHealth.record_type);

    try {
      const headers = getHeaders();
      if (isMultiSelect && selectedHealthHorses.length > 0) {
        // Use batch endpoint
        const response = await fetch(`${API}/health-records/batch`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            horse_ids: selectedHealthHorses,
            record_type: newHealth.record_type,
            farrier_type: newHealth.farrier_type,
            provider: newHealth.provider,
            date: newHealth.date,
            next_due_date: newHealth.next_due_date,
            notes: newHealth.notes
          })
        });
        if (response.ok) {
          toast.success(`Added ${selectedHealthHorses.length} health record(s)!`);
          setHealthDialogOpen(false);
          setNewHealth({ horse_id: '', record_type: 'vaccination', vet_visit_type: null, farrier_type: null, provider: '', date: '', next_due_date: '', notes: '' });
          setSelectedHealthHorses([]);
          fetchData();
        }
      } else if (newHealth.horse_id) {
        // Single record
        const response = await fetch(`${API}/health-records`, {
          method: 'POST',
          headers,
          body: JSON.stringify(newHealth)
        });
        if (response.ok) {
          toast.success('Health record added!');
          setHealthDialogOpen(false);
          setNewHealth({ horse_id: '', record_type: 'vaccination', vet_visit_type: null, farrier_type: null, provider: '', date: '', next_due_date: '', notes: '' });
          fetchData();
        }
      } else {
        toast.error('Please select at least one horse');
      }
    } catch (error) {
      toast.error('Failed to add health record');
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      const headers = getHeaders();
      const response = await fetch(`${API}/expenses`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...newExpense, amount: parseFloat(newExpense.amount) })
      });
      if (response.ok) {
        toast.success('Expense added!');
        setExpenseDialogOpen(false);
        setNewExpense({ horse_id: null, category: '', amount: '', vendor: '', date: '', notes: '' });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (selectedEventHorses.length === 0) {
      toast.error('Please select at least one horse');
      return;
    }
    try {
      const headers = getHeaders();
      const response = await fetch(`${API}/event-logs/batch`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          horse_ids: selectedEventHorses,
          ...newEvent
        })
      });
      if (response.ok) {
        toast.success(`Added event for ${selectedEventHorses.length} horse(s)!`);
        setEventDialogOpen(false);
        setNewEvent({ event_name: '', location: '', discipline: '', event_class: '', date: '', notes: '' });
        setSelectedEventHorses([]);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to add event');
    }
  };

  // Edit handlers for dashboard items
  const handleEditRide = async (e) => {
    e.preventDefault();
    if (!selectedRide) return;
    try {
      const headers = getHeaders();
      const response = await fetch(`${API}/ride-logs/${selectedRide.ride_id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ ...selectedRide, duration: selectedRide.duration ? parseInt(selectedRide.duration) : null })
      });
      if (response.ok) {
        toast.success('Ride updated!');
        setViewRideDialogOpen(false);
        setSelectedRide(null);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update ride');
    }
  };

  const handleEditHealth = async (e) => {
    e.preventDefault();
    if (!selectedHealth) return;
    try {
      const headers = getHeaders();
      const response = await fetch(`${API}/health-records/${selectedHealth.record_id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(selectedHealth)
      });
      if (response.ok) {
        toast.success('Health record updated!');
        setViewHealthDialogOpen(false);
        setSelectedHealth(null);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update health record');
    }
  };

  const handleEditExpense = async (e) => {
    e.preventDefault();
    if (!selectedExpense) return;
    try {
      const headers = getHeaders();
      const response = await fetch(`${API}/expenses/${selectedExpense.expense_id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ ...selectedExpense, amount: parseFloat(selectedExpense.amount) })
      });
      if (response.ok) {
        toast.success('Expense updated!');
        setViewExpenseDialogOpen(false);
        setSelectedExpense(null);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update expense');
    }
  };

  const handleEditEvent = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    try {
      const headers = getHeaders();
      const response = await fetch(`${API}/event-logs/${selectedEvent.event_id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(selectedEvent)
      });
      if (response.ok) {
        toast.success('Event updated!');
        setViewEventDialogOpen(false);
        setSelectedEvent(null);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  const getRecordTypeIcon = (type) => {
    switch (type) {
      case 'vaccination': return <Syringe className="w-4 h-4" />;
      case 'farrier': return <Scissors className="w-4 h-4" />;
      case 'dentist': return <Stethoscope className="w-4 h-4" />;
      case 'worming': return <Activity className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const handleDeleteReminder = async (recordId) => {
    try {
      const headers = getHeaders();
      const response = await fetch(`${API}/health-records/${recordId}`, {
        method: 'DELETE',
        headers
      });
      if (response.ok) {
        setReminders(reminders.filter(r => r.record_id !== recordId));
        toast.success('Reminder deleted');
      } else {
        toast.error('Failed to delete reminder');
      }
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const handleCompleteReminder = async (record) => {
    try {
      const headers = getHeaders();
      // Update the health record to clear the next_due_date and set today's date
      const today = new Date().toISOString().split('T')[0];
      const response = await fetch(`${API}/health-records/${record.record_id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          ...record,
          date: today,
          next_due_date: null  // Clear the reminder
        })
      });
      if (response.ok) {
        setReminders(reminders.filter(r => r.record_id !== record.record_id));
        toast.success(`${formatRecordType(record.record_type)} marked complete for ${record.horse_name}`);
        fetchData(); // Refresh to show updated recent activity
      } else {
        toast.error('Failed to complete reminder');
      }
    } catch (error) {
      toast.error('Failed to complete reminder');
    }
  };

  const getRecordTypeClass = (type) => {
    switch (type) {
      case 'vaccination': return 'health-vaccination';
      case 'farrier': return 'health-farrier';
      case 'dentist': return 'health-dentist';
      case 'worming': return 'health-worming';
      case 'injury': return 'health-injury';
      case 'medication': return 'health-medication';
      default: return '';
    }
  };

  const isUrgent = (dateStr) => {
    if (!dateStr) return false;
    const date = parseISO(dateStr);
    const soon = addDays(new Date(), 7);
    return isBefore(date, soon);
  };

  // Handle transfer acceptance
  const handleAcceptTransfer = async (transferId) => {
    try {
      const headers = getHeaders();
      const response = await fetch(`${API}/transfers/${transferId}/accept`, {
        method: 'POST',
        headers
      });
      if (response.ok) {
        setPendingTransfers(prev => prev.filter(t => t.transfer_id !== transferId));
        toast.success('Transfer accepted! Horse added to your account.');
        fetchData(); // Refresh to get the new horse
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to accept transfer');
      }
    } catch (error) {
      toast.error('Failed to accept transfer');
    }
  };

  // Handle transfer decline
  const handleDeclineTransfer = async (transferId) => {
    try {
      const headers = getHeaders();
      const response = await fetch(`${API}/transfers/${transferId}/decline`, {
        method: 'POST',
        headers
      });
      if (response.ok) {
        setPendingTransfers(prev => prev.filter(t => t.transfer_id !== transferId));
        toast.success('Transfer declined');
      } else {
        toast.error('Failed to decline transfer');
      }
    } catch (error) {
      toast.error('Failed to decline transfer');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 space-y-6">
          <div className="h-8 w-48 skeleton rounded-lg" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-40 skeleton rounded-xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-muted-foreground mt-1">
              {horses.length} {horses.length === 1 ? 'horse' : 'horses'} in your stable
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/horses">
              <Button variant="outline" className="gap-2" data-testid="view-horses-btn">
                View All Horses
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="quick-stats">
          <Card className="border-0 shadow-card bg-gradient-to-br from-emerald-100 to-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Horses</p>
                  <p className="text-2xl font-bold text-emerald-700 mt-1">{horses.length}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-emerald-200 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-emerald-700" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-card bg-gradient-to-br from-amber-500/5 to-amber-500/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Due Soon</p>
                  <p className="text-2xl font-bold text-amber-600 mt-1">{reminders.length}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
              </div>
              {reminders.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2 truncate">
                  Next: {reminders[0]?.record_type || 'Health check'}
                </p>
              )}
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-card bg-gradient-to-br from-saddle/5 to-saddle/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">This Week</p>
                  <p className="text-2xl font-bold text-saddle mt-1">
                    {recentActivity.rides?.filter(r => {
                      if (!r.date) return false;
                      const rideDate = parseISO(r.date);
                      const weekAgo = addDays(new Date(), -7);
                      return rideDate >= weekAgo;
                    }).length || 0}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-saddle/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-saddle" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">rides logged</p>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-card bg-gradient-to-br from-green-500/5 to-green-500/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Events</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">{recentActivity.events?.length || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">upcoming</p>
            </CardContent>
          </Card>
        </div>

        {/* Pending Transfer Notifications */}
        {pendingTransfers.length > 0 && (
          <Card className="border-l-4 border-l-forest bg-forest/5 shadow-card" data-testid="pending-transfers-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Send className="w-5 h-5 text-forest" />
                Incoming Horse Transfers ({pendingTransfers.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {pendingTransfers.map(transfer => (
                <div key={transfer.transfer_id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div>
                    <p className="font-medium">Horse transfer request</p>
                    <p className="text-sm text-muted-foreground">
                      From: {transfer.from_user_id} • {transfer.created_at ? format(parseISO(transfer.created_at), 'MMM d, yyyy') : ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      className="bg-forest hover:bg-forest-dark"
                      onClick={() => handleAcceptTransfer(transfer.transfer_id)}
                      data-testid={`accept-transfer-${transfer.transfer_id}`}
                    >
                      Accept
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" data-testid={`decline-transfer-${transfer.transfer_id}`}>
                          Decline
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Decline Transfer?</AlertDialogTitle>
                          <AlertDialogDescription>Are you sure you want to decline this horse transfer request?</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeclineTransfer(transfer.transfer_id)} className="bg-destructive text-destructive-foreground">Decline</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <Button 
            variant="outline" 
            className="w-full h-auto py-4 flex flex-col gap-2 relative hover:bg-forest hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed" 
            onClick={() => requireSubscription(() => setAddHorseDialogOpen(true))} 
            disabled={!canAddEdit()}
            data-testid="quick-add-horse-btn"
          >
            {!canAddEdit() && <Lock className="w-4 h-4 absolute top-2 right-2 text-amber-500" />}
            <Plus className="w-5 h-5" />
            <span className="text-sm">Add Horse</span>
          </Button>
          <Button 
            variant="outline" 
            className="w-full h-auto py-4 flex flex-col gap-2 relative hover:bg-saddle hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed" 
            onClick={() => requireSubscription(() => setRideDialogOpen(true))} 
            disabled={!canAddEdit()}
            data-testid="quick-add-ride-btn"
          >
            {!canAddEdit() && <Lock className="w-4 h-4 absolute top-2 right-2 text-amber-500" />}
            <Activity className="w-5 h-5" />
            <span className="text-sm">Log Ride</span>
          </Button>
          <Button 
            variant="outline" 
            className="w-full h-auto py-4 flex flex-col gap-2 relative hover:bg-terracotta hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed" 
            onClick={() => requireSubscription(() => setHealthDialogOpen(true))} 
            disabled={!canAddEdit()}
            data-testid="quick-add-health-btn"
          >
            {!canAddEdit() && <Lock className="w-4 h-4 absolute top-2 right-2 text-amber-500" />}
            <Stethoscope className="w-5 h-5" />
            <span className="text-sm">Health Record</span>
          </Button>
          <Button 
            variant="outline" 
            className="w-full h-auto py-4 flex flex-col gap-2 relative hover:bg-forest hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed" 
            onClick={() => requireSubscription(() => setExpenseDialogOpen(true))} 
            disabled={!canAddEdit()}
            data-testid="quick-add-expense-btn"
          >
            {!canAddEdit() && <Lock className="w-4 h-4 absolute top-2 right-2 text-amber-500" />}
            <DollarSign className="w-5 h-5" />
            <span className="text-sm">Add Expense</span>
          </Button>
          <Button 
            variant="outline" 
            className="w-full h-auto py-4 flex flex-col gap-2 relative hover:bg-amber-600 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed" 
            onClick={() => requireSubscription(() => setEventDialogOpen(true))} 
            disabled={!canAddEdit()}
            data-testid="quick-add-event-btn"
          >
            {!canAddEdit() && <Lock className="w-4 h-4 absolute top-2 right-2 text-amber-500" />}
            <Trophy className="w-5 h-5" />
            <span className="text-sm">Add Event</span>
          </Button>
          <Link to="/goals" className="w-full">
            <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2 hover:bg-emerald-600 hover:text-white transition-colors" data-testid="quick-goals-btn">
              <Target className="w-5 h-5" />
              <span className="text-sm">Goals</span>
            </Button>
          </Link>
          <Link to="/rider-profiles" className="w-full">
            <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2 hover:bg-blue-600 hover:text-white transition-colors" data-testid="quick-rider-btn">
              <User className="w-5 h-5" />
              <span className="text-sm">Riders</span>
            </Button>
          </Link>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Reminders - Takes more space */}
          <Card className="lg:col-span-8 shadow-card border-0 bg-white" data-testid="reminders-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Clock className="w-5 h-5 text-saddle" />
                Upcoming Care
              </CardTitle>
              <Link to="/reminders">
                <Button variant="ghost" size="sm" className="text-muted-foreground">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {reminders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No upcoming care reminders</p>
                  <p className="text-sm">Add health records to see reminders here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {reminders.slice(0, 10).map((record, i) => (
                    <div 
                      key={record.record_id || i}
                      className={`flex items-center gap-4 p-3 rounded-lg ${getRecordTypeClass(record.record_type)} animate-slideUp`}
                      style={{ animationDelay: `${i * 0.05}s` }}
                      data-testid={`reminder-item-${record.record_id}`}
                    >
                      <div 
                        className="p-2 rounded-lg bg-white/50 cursor-pointer"
                        onClick={() => { setSelectedHealth(record); setViewHealthDialogOpen(true); }}
                      >
                        {getRecordTypeIcon(record.record_type)}
                      </div>
                      <div 
                        className="flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => { setSelectedHealth(record); setViewHealthDialogOpen(true); }}
                      >
                        <p className="font-medium">{formatRecordType(record.record_type)}</p>
                        <p className="text-sm opacity-80">{record.horse_name}</p>
                        {record.notes && <p className="text-xs opacity-70 truncate mt-1">{record.notes}</p>}
                      </div>
                      <div className="text-right mr-2">
                        <p className="text-sm font-medium">
                          {record.next_due_date && format(parseISO(record.next_due_date), 'MMM d')}
                        </p>
                        {isUrgent(record.next_due_date) && (
                          <Badge variant="destructive" className="text-xs">Due Soon</Badge>
                        )}
                      </div>
                      {/* Complete Button */}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-muted-foreground hover:text-green-600 hover:bg-green-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCompleteReminder(record);
                        }}
                        title="Mark Complete"
                        data-testid={`complete-reminder-${record.record_id}`}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                      {/* Delete Button */}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-white/50"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`delete-reminder-${record.record_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Reminder</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this {record.record_type} reminder for {record.horse_name}? This will delete the associated health record.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteReminder(record.record_id)}
                              className="bg-destructive hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card className="lg:col-span-4 shadow-card border-0 bg-white" data-testid="events-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Trophy className="w-5 h-5 text-saddle" />
                Upcoming Events
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentActivity.events.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Calendar className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No upcoming events</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentActivity.events.slice(0, 4).map((event, i) => (
                    <div 
                      key={event.event_id || i}
                      className="p-3 rounded-lg bg-muted/50 animate-slideUp cursor-pointer hover:bg-muted/70 transition-colors"
                      style={{ animationDelay: `${i * 0.05}s` }}
                      onClick={() => { setSelectedEvent(event); setViewEventDialogOpen(true); }}
                      data-testid={`event-item-${event.event_id}`}
                    >
                      <p className="font-medium truncate">{event.event_name}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-sm text-muted-foreground">{event.horse_name}</span>
                        <span className="text-sm font-medium">
                          {event.date && format(parseISO(event.date), 'MMM d')}
                        </span>
                      </div>
                      {event.notes && <p className="text-xs text-muted-foreground truncate mt-1">{event.notes}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity (Rides + Health) */}
          <Card className="lg:col-span-6 shadow-card border-0 bg-white" data-testid="activity-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Activity className="w-5 h-5 text-forest" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!recentActivity.recent_activity || recentActivity.recent_activity.length === 0) ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Activity className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No recent activity</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentActivity.recent_activity.slice(0, 10).map((item, i) => (
                    <div 
                      key={item.ride_id || item.record_id || i}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/30 animate-slideUp cursor-pointer hover:bg-muted/50 transition-colors"
                      style={{ animationDelay: `${i * 0.05}s` }}
                      onClick={() => {
                        if (item.activity_type === 'ride') {
                          setSelectedRide({...item, duration: item.duration || '', ride_types: item.ride_types || (item.ride_type ? [item.ride_type] : [])});
                          setViewRideDialogOpen(true);
                        } else {
                          setSelectedHealth(item);
                          setViewHealthDialogOpen(true);
                        }
                      }}
                      data-testid={`activity-item-${item.ride_id || item.record_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-1.5 rounded ${item.activity_type === 'ride' ? 'bg-forest/10' : 'bg-blue-100'}`}>
                          {item.activity_type === 'ride' ? (
                            <Activity className="w-4 h-4 text-forest" />
                          ) : (
                            getRecordTypeIcon(item.record_type)
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{item.horse_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.activity_type === 'ride' 
                              ? ((item.ride_types && item.ride_types.length > 0) ? item.ride_types.join(', ') : (item.ride_type || 'Ride'))
                              : (item.record_type ? item.record_type.charAt(0).toUpperCase() + item.record_type.slice(1) : 'Health')}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {item.activity_date && format(parseISO(item.activity_date), 'MMM d')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Expenses */}
          <Card className="lg:col-span-6 shadow-card border-0 bg-white" data-testid="expenses-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-terracotta" />
                Recent Expenses
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentActivity.recent_expenses?.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No recent expenses</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {(recentActivity.recent_expenses || []).slice(0, 10).map((expense, i) => (
                    <div 
                      key={expense.expense_id || i}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/30 animate-slideUp cursor-pointer hover:bg-muted/50 transition-colors"
                      style={{ animationDelay: `${i * 0.05}s` }}
                      onClick={() => { setSelectedExpense({...expense, amount: expense.amount?.toString() || ''}); setViewExpenseDialogOpen(true); }}
                      data-testid={`expense-item-${expense.expense_id}`}
                    >
                      <div>
                        <p className="font-medium capitalize">{expense.category?.replace('_', ' ')}</p>
                        <p className="text-sm text-muted-foreground">{expense.horse_name || 'General'}</p>
                        {expense.notes && <p className="text-xs text-muted-foreground truncate mt-1 max-w-[200px]">{expense.notes}</p>}
                      </div>
                      <span className="font-semibold text-terracotta">
                        ${expense.amount?.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Add Dialogs */}
        <Dialog open={rideDialogOpen} onOpenChange={setRideDialogOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">Quick Log Ride</DialogTitle>
            </DialogHeader>
            <RideForm data={newRide} onChange={setNewRide} onSubmit={handleAddRide} horses={horses} riders={riders} />
          </DialogContent>
        </Dialog>

        <Dialog open={healthDialogOpen} onOpenChange={(open) => {
          setHealthDialogOpen(open);
          if (!open) setSelectedHealthHorses([]);
        }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">Quick Add Health Record</DialogTitle>
            </DialogHeader>
            <HealthForm 
              data={newHealth} 
              onChange={setNewHealth} 
              onSubmit={handleAddHealth} 
              horses={horses}
              selectedHorses={selectedHealthHorses}
              setSelectedHorses={setSelectedHealthHorses}
            />
          </DialogContent>
        </Dialog>

        <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">Quick Add Expense</DialogTitle>
            </DialogHeader>
            <ExpenseForm data={newExpense} onChange={setNewExpense} onSubmit={handleAddExpense} horses={horses} />
          </DialogContent>
        </Dialog>

        <Dialog open={eventDialogOpen} onOpenChange={(open) => {
          setEventDialogOpen(open);
          if (!open) setSelectedEventHorses([]);
        }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">Quick Add Event</DialogTitle>
            </DialogHeader>
            <EventForm 
              data={newEvent} 
              onChange={setNewEvent} 
              onSubmit={handleAddEvent} 
              horses={horses}
              selectedHorses={selectedEventHorses}
              setSelectedHorses={setSelectedEventHorses}
            />
          </DialogContent>
        </Dialog>

        {/* View/Edit Dialogs */}
        <Dialog open={viewRideDialogOpen} onOpenChange={(open) => { setViewRideDialogOpen(open); if (!open) setSelectedRide(null); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">View/Edit Ride</DialogTitle>
            </DialogHeader>
            {selectedRide && (
              <form onSubmit={handleEditRide} className="space-y-4 mt-4">
                <div className="p-3 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Horse</p>
                  <p className="font-medium">{selectedRide.horse_name}</p>
                </div>
                <div className="space-y-2">
                  <Label>Rider</Label>
                  <Input value={selectedRide.rider || ''} onChange={(e) => setSelectedRide({ ...selectedRide, rider: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input type="date" value={selectedRide.date || ''} onChange={(e) => setSelectedRide({ ...selectedRide, date: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Duration (min)</Label>
                    <Input type="number" value={selectedRide.duration || ''} onChange={(e) => setSelectedRide({ ...selectedRide, duration: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Ride Type</Label>
                  <Select value={selectedRide.ride_type || ''} onValueChange={(v) => setSelectedRide({ ...selectedRide, ride_type: v })}>
                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flatwork">Flatwork</SelectItem>
                      <SelectItem value="jumping">Jumping</SelectItem>
                      <SelectItem value="trail">Trail Ride</SelectItem>
                      <SelectItem value="lesson">Lesson</SelectItem>
                      <SelectItem value="hack">Hack</SelectItem>
                      <SelectItem value="trackwork">Trackwork</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea value={selectedRide.notes || ''} onChange={(e) => setSelectedRide({ ...selectedRide, notes: e.target.value })} />
                </div>
                <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="edit-ride-submit-btn">Save Changes</Button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={viewHealthDialogOpen} onOpenChange={(open) => { setViewHealthDialogOpen(open); if (!open) setSelectedHealth(null); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">View/Edit Health Record</DialogTitle>
            </DialogHeader>
            {selectedHealth && (
              <form onSubmit={handleEditHealth} className="space-y-4 mt-4">
                <div className="p-3 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Horse</p>
                  <p className="font-medium">{selectedHealth.horse_name}</p>
                </div>
                <div className="space-y-2">
                  <Label>Record Type</Label>
                  <Select value={selectedHealth.record_type} onValueChange={(v) => setSelectedHealth({ ...selectedHealth, record_type: v, vet_visit_type: v === 'veterinary' ? (selectedHealth.vet_visit_type || 'checkup') : null })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vaccination">Vaccination</SelectItem>
                      <SelectItem value="worming">Worming</SelectItem>
                      <SelectItem value="farrier">Farrier</SelectItem>
                      <SelectItem value="dentist">Dentist</SelectItem>
                      <SelectItem value="veterinary">Veterinary</SelectItem>
                      <SelectItem value="medication">Medication</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {selectedHealth.record_type === 'veterinary' && (
                  <div className="space-y-2">
                    <Label>Visit Type</Label>
                    <Select value={selectedHealth.vet_visit_type || 'checkup'} onValueChange={(v) => setSelectedHealth({ ...selectedHealth, vet_visit_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="illness">Illness</SelectItem>
                        <SelectItem value="injury">Injury</SelectItem>
                        <SelectItem value="pre_purchase">Pre-Purchase Examination</SelectItem>
                        <SelectItem value="checkup">Check Up</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Input value={selectedHealth.provider || ''} onChange={(e) => setSelectedHealth({ ...selectedHealth, provider: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input type="date" value={selectedHealth.date || ''} onChange={(e) => setSelectedHealth({ ...selectedHealth, date: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Next Due Date</Label>
                    <Input type="date" value={selectedHealth.next_due_date || ''} onChange={(e) => setSelectedHealth({ ...selectedHealth, next_due_date: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea value={selectedHealth.notes || ''} onChange={(e) => setSelectedHealth({ ...selectedHealth, notes: e.target.value })} />
                </div>
                <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="edit-health-submit-btn">Save Changes</Button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={viewExpenseDialogOpen} onOpenChange={(open) => { setViewExpenseDialogOpen(open); if (!open) setSelectedExpense(null); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">View/Edit Expense</DialogTitle>
            </DialogHeader>
            {selectedExpense && (
              <form onSubmit={handleEditExpense} className="space-y-4 mt-4">
                <div className="p-3 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Horse</p>
                  <p className="font-medium">{selectedExpense.horse_name || 'General'}</p>
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={selectedExpense.category || ''} onValueChange={(v) => setSelectedExpense({ ...selectedExpense, category: v })}>
                    <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vet">Vet</SelectItem>
                      <SelectItem value="farrier">Farrier</SelectItem>
                      <SelectItem value="feed">Feed</SelectItem>
                      <SelectItem value="gear">Tack</SelectItem>
                      <SelectItem value="entry_fee">Entry Fee</SelectItem>
                      <SelectItem value="boarding">Boarding</SelectItem>
                      <SelectItem value="training">Training</SelectItem>
                      <SelectItem value="lesson">Lesson</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount</Label>
                    <Input type="number" step="0.01" value={selectedExpense.amount || ''} onChange={(e) => setSelectedExpense({ ...selectedExpense, amount: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input type="date" value={selectedExpense.date || ''} onChange={(e) => setSelectedExpense({ ...selectedExpense, date: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Vendor</Label>
                  <Input value={selectedExpense.vendor || ''} onChange={(e) => setSelectedExpense({ ...selectedExpense, vendor: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea value={selectedExpense.notes || ''} onChange={(e) => setSelectedExpense({ ...selectedExpense, notes: e.target.value })} />
                </div>
                <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="edit-expense-submit-btn">Save Changes</Button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={viewEventDialogOpen} onOpenChange={(open) => { setViewEventDialogOpen(open); if (!open) setSelectedEvent(null); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">View/Edit Event</DialogTitle>
            </DialogHeader>
            {selectedEvent && (
              <form onSubmit={handleEditEvent} className="space-y-4 mt-4">
                <div className="p-3 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Horse</p>
                  <p className="font-medium">{selectedEvent.horse_name}</p>
                </div>
                <div className="space-y-2">
                  <Label>Event Name</Label>
                  <Input value={selectedEvent.event_name || ''} onChange={(e) => setSelectedEvent({ ...selectedEvent, event_name: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input type="date" value={selectedEvent.date || ''} onChange={(e) => setSelectedEvent({ ...selectedEvent, date: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input value={selectedEvent.location || ''} onChange={(e) => setSelectedEvent({ ...selectedEvent, location: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Discipline</Label>
                    <Input value={selectedEvent.discipline || ''} onChange={(e) => setSelectedEvent({ ...selectedEvent, discipline: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Class</Label>
                    <Input value={selectedEvent.event_class || ''} onChange={(e) => setSelectedEvent({ ...selectedEvent, event_class: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Placing</Label>
                    <Input value={selectedEvent.placing || ''} onChange={(e) => setSelectedEvent({ ...selectedEvent, placing: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Score</Label>
                    <Input value={selectedEvent.score || ''} onChange={(e) => setSelectedEvent({ ...selectedEvent, score: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea value={selectedEvent.notes || ''} onChange={(e) => setSelectedEvent({ ...selectedEvent, notes: e.target.value })} />
                </div>
                <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="edit-event-submit-btn">Save Changes</Button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Horse Dialog */}
        <Dialog open={addHorseDialogOpen} onOpenChange={setAddHorseDialogOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-serif">Add New Horse</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddHorse} className="space-y-4 mt-4">
              {/* Profile Photo Upload */}
              <div className="space-y-2">
                <Label>Profile Photo</Label>
                <div className="flex items-center gap-4">
                  <div 
                    className="relative w-24 h-24 rounded-lg border-2 border-dashed border-muted-foreground/30 hover:border-forest/50 transition-colors cursor-pointer overflow-hidden bg-muted/30 flex items-center justify-center"
                    onClick={() => profilePhotoInputRef.current?.click()}
                  >
                    {profilePhotoPreview ? (
                      <>
                        <img 
                          src={profilePhotoPreview} 
                          alt="Preview" 
                          className="w-full h-full object-cover"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute top-1 right-1 h-6 w-6 bg-black/50 hover:bg-black/70 text-white rounded-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            clearProfilePhoto();
                          }}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </>
                    ) : (
                      <div className="text-center p-2">
                        <Camera className="w-8 h-8 mx-auto text-muted-foreground/50" />
                        <span className="text-xs text-muted-foreground">Add photo</span>
                      </div>
                    )}
                  </div>
                  <input
                    ref={profilePhotoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePhotoSelect}
                    className="hidden"
                  />
                  <p className="text-xs text-muted-foreground flex-1">
                    Optional: Add an ID photo for your horse.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Registered Name *</Label>
                  <Input 
                    value={newHorse.registered_name} 
                    onChange={(e) => setNewHorse({...newHorse, registered_name: e.target.value})}
                    required
                    data-testid="quick-horse-registered-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Stable Name *</Label>
                  <Input 
                    value={newHorse.stable_name} 
                    onChange={(e) => setNewHorse({...newHorse, stable_name: e.target.value})}
                    required
                    data-testid="quick-horse-stable-name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Breed</Label>
                  <Input 
                    value={newHorse.breed || ''} 
                    onChange={(e) => setNewHorse({...newHorse, breed: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Colour</Label>
                  <Input 
                    value={newHorse.colour || ''} 
                    onChange={(e) => setNewHorse({...newHorse, colour: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sex</Label>
                  <Select value={newHorse.sex || ''} onValueChange={(v) => setNewHorse({...newHorse, sex: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select sex" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mare">Mare</SelectItem>
                      <SelectItem value="gelding">Gelding</SelectItem>
                      <SelectItem value="stallion">Stallion</SelectItem>
                      <SelectItem value="colt">Colt</SelectItem>
                      <SelectItem value="filly">Filly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date of Birth</Label>
                  <Input 
                    type="date"
                    value={newHorse.dob || ''} 
                    onChange={(e) => setNewHorse({...newHorse, dob: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Height</Label>
                  <Input 
                    value={newHorse.height || ''} 
                    onChange={(e) => setNewHorse({...newHorse, height: e.target.value})}
                    placeholder="e.g., 16.2hh"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Microchip</Label>
                  <Input 
                    value={newHorse.microchip || ''} 
                    onChange={(e) => setNewHorse({...newHorse, microchip: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Registration Number</Label>
                  <Input 
                    value={newHorse.registration_number || ''} 
                    onChange={(e) => setNewHorse({...newHorse, registration_number: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Registration Society</Label>
                  <Input 
                    value={newHorse.registration_society || ''} 
                    onChange={(e) => setNewHorse({...newHorse, registration_society: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sire (Father)</Label>
                  <Input 
                    value={newHorse.sire || ''} 
                    onChange={(e) => setNewHorse({...newHorse, sire: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Dam (Mother)</Label>
                  <Input 
                    value={newHorse.dam || ''} 
                    onChange={(e) => setNewHorse({...newHorse, dam: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Breeder</Label>
                  <Input 
                    value={newHorse.breeder || ''} 
                    onChange={(e) => setNewHorse({...newHorse, breeder: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Owner</Label>
                  <Input 
                    value={newHorse.owner || ''} 
                    onChange={(e) => setNewHorse({...newHorse, owner: e.target.value})}
                  />
                </div>
              </div>
              <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="quick-horse-submit-btn" disabled={isAddingHorse}>
                {isAddingHorse ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding Horse...
                  </>
                ) : (
                  'Add Horse'
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
